import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Col, Flex, Row, Switch } from 'antd';
import { useEffect, useState } from 'react';
import { RectangleDashed } from '@phosphor-icons/react';
import { ControlledInput, ControlledNumberInput } from '@elements/controlled-fields';
import { HoverBorderButton, ShadowBox, StyledButton } from '../../elements';
import { CommonUtility, ToastMessage, ZoneService } from '../../utility';
import { LoaderBar } from '../../components';
import { useWarehouseDetails } from '../../screens/app/settings/warehouse';
import { useNavigate } from 'react-router-dom';
import {
  ConfirmDeleteLocationPopup,
  DeleteLocationSchema,
} from '@page-components/warehouse/DeleteLocationPopup';

const RackShema = yup.object().shape({
  code: yup.string().required('Code is required'),
  priority: yup
    .number()
    .nullable()
    .transform((_, val) =>
      val === '' ||
      val === null ||
      val === undefined ||
      (typeof val === 'number' && isNaN(val))
        ? null
        : Number(val),
    )
    .integer('Priority must be an integer')
    .min(1, 'Priority must be at least 1')
    .max(999, 'Priority must be at most 999')
    .optional(),
  width: yup
    .number()
    .positive('Width must be greater than 0')
    .typeError('Width is required')
    .required('Width is required'),
  height: yup
    .number()
    .positive('Height must be greater than 0')
    .typeError('Height is required')
    .required('Height is required'),
  length: yup
    .number()
    .positive('Length must be greater than 0')
    .typeError('Length is required')
    .required('Length is required'),
});

export function ZoneDimensionsDetails({
  data,
  detailLoading,
  refresh,
  hasAisles,
  toggleAisles,
}) {
  const { warehouseData } = useWarehouseDetails();
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const navigate = useNavigate();
  const { refreshWarehouse } = useWarehouseDetails();
  const [errorObject, setErrorObject] = useState(null);

  const openDeletePopup = () => {
    setShowDeletePopup(true);
  };

  const closeDeletePopup = () => {
    setShowDeletePopup(false);
  };

  const openWarehouseScreen = () => {
    navigate(`/app/settings/warehouse`);
  };

  const onDelete = async () => {
    try {
      setLoading(true);
      await ZoneService.remove(data?._id);
      ToastMessage.success('Zone successfully deleted.');
      openWarehouseScreen();
      refreshWarehouse();
      closeDeletePopup();
    } catch (error) {
      const errorObject = DeleteLocationSchema.safeParse(error);
      if (errorObject.success) {
        setErrorObject(errorObject.data);
      } else {
        ToastMessage.apiError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const [loading, setLoading] = useState(false);
  const {
    control,
    formState: { dirtyFields },
    reset,
    handleSubmit,
  } = useForm({
    resolver: yupResolver(RackShema),
    shouldUnregister: true,
  });

  useEffect(() => {
    if (CommonUtility.isValidObject(data)) resetForm();
  }, [data]);

  const resetForm = () => {
    reset({
      code: data?.code,
      priority: data?.priority ?? 5,
      ...data?.dimensions,
    });
  };

  const onSubmit = async formData => {
    try {
      setLoading(true);
      const payload = {
        code: formData.code,
        priority: formData.priority,
        dimensions: {
          length: formData.length,
          width: formData.width,
          height: formData.height,
        },
      };

      const res = await ZoneService.updateZone(data?._id, payload);
      ToastMessage.success('Zone successfully updated.');
      refresh();
      reset({
        code: res.code,
        priority: res.priority ?? formData.priority,
        ...res?.dimensions,
      });
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ShadowBox className="position-relative">
      {detailLoading && <LoaderBar />}
      <form className="mt-2" onSubmit={handleSubmit(onSubmit)}>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <ControlledInput
              control={control}
              name="code"
              label="Code"
              placeholder="Enter zone code"
              required
            />
          </Col>
          <Col xs={24} md={12}>
            <ControlledNumberInput
              control={control}
              name="priority"
              label="Priority"
              placeholder="1–999"
              min={1}
              max={999}
              step={1}
            />
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <ControlledNumberInput
              control={control}
              name="length"
              label={`Length (${warehouseData?.dimensions?.measurementUnit})`}
              placeholder="0.00"
              required
              min={0}
              step={0.01}
            />
          </Col>
          <Col xs={24} sm={8}>
            <ControlledNumberInput
              control={control}
              name="width"
              label={`Width (${warehouseData?.dimensions?.measurementUnit})`}
              placeholder="0.00"
              required
              min={0}
              step={0.01}
            />
          </Col>
          <Col xs={24} sm={8}>
            <ControlledNumberInput
              control={control}
              name="height"
              label={`Height (${warehouseData?.dimensions?.measurementUnit})`}
              placeholder="0.00"
            />
          </Col>
        </Row>
        {CommonUtility.isValidObject(dirtyFields) && (
          <div className="d-flex justify-content-end align-items-center flex-wrap mt-3 mb-3">
            <HoverBorderButton disabled={loading} type="button" onClick={resetForm}>
              Cancel
            </HoverBorderButton>
            <StyledButton loading={loading} type="submit" className="ml-2">
              Update
            </StyledButton>
          </div>
        )}
      </form>
      <Flex align="center" gap={8} className="mb-3 mt-3">
        <Switch checked={hasAisles} onChange={toggleAisles} />
        <span>Sort this Zone into Aisles</span>
      </Flex>
      <Button variant="outlined" danger onClick={openDeletePopup}>
        Delete {data?.code}
      </Button>
      <ConfirmDeleteLocationPopup
        open={showDeletePopup}
        onClose={closeDeletePopup}
        onConfirm={onDelete}
        title={`Delete ${data?.code}`}
        message={`Are you sure you want to delete ${data?.code}?`}
        loading={loading}
        errorObject={errorObject}
      />
    </ShadowBox>
  );
}
